/**
 * SmartMessageBubble
 *
 * Renders a smart_message type conversation message in two variants:
 *
 * Variant is driven by currentRole only — never by isOwn. Staff users can
 * role-switch to "parent" and need the action card for their own sent
 * messages, because in that role they are the recipient, not the sender.
 *
 * Teacher/Staff/Admin variant (currentRole !== 'parent'):
 * - Collapsed card showing the message title (text)
 * - Click to expand: subscribes to live smart message details,
 *   shows body text and a "View full tracker" link
 * - Unsubscribes when collapsed again
 *
 * Parent variant (currentRole === 'parent'):
 * - Shows title + body text inline
 * - Response buttons from message.responseOptions
 * - On respond: calls SmartMessageService.respondToSmartMessage
 * - After responding: shows confirmation text
 * - Parents never see the recipient list — their job is to respond.
 *
 * @module features/messaging/components/SmartMessageBubble
 */

import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import {
  listenToSmartMessageDetails,
  listenToSmartMessageRecipient,
  respondToSmartMessage,
  uploadSmartMessageAttachment,
} from '@/services/messaging/SmartMessageService';
import { isBlockBody, computeRecipientStatus, blocksToPlainText } from '@/models/smartMessageBlocks';
import SmartMessageBlockRenderer, { SmartMessageBlockStats } from './SmartMessageBlockRenderer';
import SmartMessageSignModal from './SmartMessageSignModal';
import AttendanceReasonForm from '@/components/messaging/responseForms/AttendanceReasonForm';

export default function SmartMessageBubble({ message, isOwn, currentRole }) {
  const isTeacherVariant = currentRole !== 'parent';

  // bodyVersion 2 — the card carries the full Block[] as `body`
  const isBlockBased = isBlockBody(message.body);

  // The `smart_${uid}` inbox is shared between the user's staff and parent
  // roles. The CF writes a tracker copy (no studentId) for the sender so
  // staff mode can monitor responses, and per-parent action cards (with
  // studentId) for linked parents. In parent mode the tracker copy isn't
  // for them — they weren't a recipient — so hide it. In staff mode, the
  // per-parent action cards are noise next to the tracker, so hide those.
  if (isTeacherVariant && message.studentId) return null;
  if (!isTeacherVariant && !message.studentId) return null;

  // Per-user `smart_messages` inbox is user-scoped so its conversation has no
  // schoolId. The smart_message card carries its own schoolId — that's the
  // source of truth, not the conversation.
  const schoolId = message.schoolId;

  // ── Teacher variant state ──────────────────────────────────────────────────
  const [isExpanded, setIsExpanded] = useState(false);
  const [details, setDetails] = useState(null);

  // ── Parent variant state ───────────────────────────────────────────────────
  // The recipient doc is the transactional source of truth — its `status`
  // flips to "responded" in the same transaction as the response. Subscribe
  // to it directly. Fast-path fallback: derive from message-doc fields so
  // the bubble doesn't flash "Agree/Decline" on first paint before the
  // recipient listener fires.
  const messageDocResponse = useMemo(() => {
    const inApp = message.delivery?.inApp?.response;
    if (inApp?.actionId) return { optionId: inApp.actionId, optionLabel: inApp.value };
    if (message.response) return message.response;
    return null;
  }, [message.delivery?.inApp?.response, message.response]);

  const [recipientData, setRecipientData] = useState(null);
  const [optimisticResponse, setOptimisticResponse] = useState(null);
  const [optimisticBlockResponses, setOptimisticBlockResponses] = useState({});
  const [signingBlock, setSigningBlock] = useState(null);
  const [responding, setResponding] = useState(false);
  const [pendingOptId, setPendingOptId] = useState(null);
  const [respondError, setRespondError] = useState(null);

  const recipientResponse =
    recipientData?.status === 'responded' ? recipientData.response : null;
  const confirmedResponse =
    recipientResponse || messageDocResponse || optimisticResponse;
  const responded = !!confirmedResponse;

  // v2 — per-block responses off the recipient doc; optimistic layer keeps the
  // block locked between submit and the listener's next snapshot.
  const blockResponses = useMemo(
    () => ({ ...(recipientData?.responses || {}), ...optimisticBlockResponses }),
    [recipientData?.responses, optimisticBlockResponses],
  );
  const blockStatus = isBlockBased
    ? computeRecipientStatus(message.body, blockResponses)
    : null;

  // Subscribe to live smart message details only when teacher variant is expanded
  useEffect(() => {
    if (!isTeacherVariant) return;
    if (!isExpanded) return;

    const unsub = listenToSmartMessageDetails(schoolId, message.smartMessageId, (data) => {
      setDetails(data);
    });

    return () => {
      unsub();
    };
  }, [isExpanded, isTeacherVariant, schoolId, message.smartMessageId]);

  // Parent variant: subscribe to the recipient doc — source of truth for
  // "did I respond?". Independent of message-doc state.
  useEffect(() => {
    if (isTeacherVariant) return;
    if (!schoolId || !message.smartMessageId || !message.studentId) return;
    const unsub = listenToSmartMessageRecipient(
      schoolId,
      message.smartMessageId,
      message.studentId,
      setRecipientData,
    );
    return () => {
      unsub();
    };
  }, [isTeacherVariant, schoolId, message.smartMessageId, message.studentId]);

  const handleToggleExpand = () => {
    setIsExpanded((prev) => !prev);
  };

  const handleRespond = async (option) => {
    if (responding || responded) return;
    setResponding(true);
    setPendingOptId(option.id);
    setRespondError(null);
    try {
      await respondToSmartMessage(schoolId, {
        smartMessageId: message.smartMessageId,
        optionId: option.id,
        studentId: message.studentId,
      });
      setOptimisticResponse({ optionId: option.id, optionLabel: option.label });
    } catch (err) {
      // The recipient doc already says responded — that's truth, not an error.
      // Treat it as a success so the UI flips to the confirmed state.
      const code = err?.code || '';
      const msg = err?.message || '';
      if (code === 'functions/already-exists' || /already responded/i.test(msg)) {
        setOptimisticResponse({ optionId: option.id, optionLabel: option.label });
      } else {
        console.error('[SmartMessageBubble] respond error:', err);
        setRespondError(msg || 'Could not submit response. Please try again.');
      }
    } finally {
      setResponding(false);
      setPendingOptId(null);
    }
  };

  // v2 — one call per block; the renderer owns busy/error display and locks
  // the block on success. First-respond-wins: already-exists means someone in
  // the family beat us to it, which is a confirmed state, not an error.
  const handleBlockRespond = async (blockId, value) => {
    try {
      await respondToSmartMessage(schoolId, {
        smartMessageId: message.smartMessageId,
        blockId,
        value,
        studentId: message.studentId,
      });
      setOptimisticBlockResponses((prev) => ({ ...prev, [blockId]: { value, respondedAt: null } }));
    } catch (err) {
      const code = err?.code || '';
      const msg = err?.message || '';
      if (code === 'functions/already-exists' || /already been answered/i.test(msg)) {
        setOptimisticBlockResponses((prev) => ({ ...prev, [blockId]: { value, respondedAt: null } }));
        return;
      }
      console.error('[SmartMessageBubble] block respond error:', err);
      throw err;
    }
  };

  // ── Teacher / Staff / Admin variant ───────────────────────────────────────
  if (isTeacherVariant) {
    return (
      <div className={`flex mb-1 ${isOwn ? 'justify-end' : 'justify-start'}`}>
        <div className="max-w-xs lg:max-w-md bg-primary text-primary-content rounded-2xl overflow-hidden">
          {/* Header — always visible, click to toggle */}
          <div
            className="flex items-center justify-between gap-2 p-3 cursor-pointer select-none"
            onClick={handleToggleExpand}
          >
            <p className="font-medium text-sm">{message.text}</p>
            {isExpanded ? (
              <ChevronUpIcon className="w-4 h-4 shrink-0 opacity-70" />
            ) : (
              <ChevronDownIcon className="w-4 h-4 shrink-0 opacity-70" />
            )}
          </div>

          {/* Expanded detail section */}
          <AnimatePresence>
            {isExpanded && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="border-t border-primary-content/20 px-3 pb-3 overflow-hidden"
              >
                {details ? (
                  <>
                    {Array.isArray(details.message?.body) ? (
                      <>
                        <p className="text-sm mt-2 line-clamp-3 opacity-90">
                          {blocksToPlainText(details.message.body)}
                        </p>
                        <div className="mt-2">
                          <SmartMessageBlockStats
                            blocks={details.message.body}
                            blockResponseCounts={details.message.blockResponseCounts}
                          />
                        </div>
                      </>
                    ) : (
                      <p className="text-sm mt-2">{details.message?.body}</p>
                    )}
                    <div className="mt-2">
                      <Link
                        to={`/messaging/smart-messages/${message.smartMessageId}`}
                        className="text-xs underline opacity-80 hover:opacity-100"
                      >
                        View full tracker
                      </Link>
                    </div>
                  </>
                ) : (
                  <p className="text-xs mt-2 opacity-60">Loading…</p>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    );
  }

  // ── Parent variant ─────────────────────────────────────────────────────────
  const sentAtDate = message.sentAt?.toDate?.();
  const sentAtLabel = sentAtDate
    ? sentAtDate.toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      })
    : null;
  const studentLabel = message.studentName
    ? message.className
      ? `${message.studentName} · ${message.className}`
      : message.studentName
    : null;
  const recipientLabel =
    typeof message.recipientCount === 'number' && message.recipientCount > 0
      ? message.recipientCount === 1
        ? '1 family'
        : `${message.recipientCount} families`
      : null;
  const showMeta = !!(message.senderName || studentLabel || recipientLabel);

  return (
    <div className="flex justify-start mb-1">
      <div className="w-full bg-base-100 border border-base-300 border-l-4 border-l-primary rounded-2xl p-4">
        <div className="flex items-center justify-between mb-2">
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary/10 text-primary text-[10px] font-bold uppercase tracking-wide">
            Smart Message
          </span>
          {sentAtLabel && (
            <span className="text-[10px] text-base-content/60">{sentAtLabel}</span>
          )}
        </div>

        <p className="font-semibold text-base">{message.text}</p>
        {isBlockBased ? (
          <div className="mt-3">
            <SmartMessageBlockRenderer
              blocks={message.body}
              responses={blockResponses}
              onRespond={handleBlockRespond}
              onSign={(block) => setSigningBlock(block)}
              uploadFile={(file, onProgress) =>
                uploadSmartMessageAttachment(schoolId, file, onProgress).then((r) => ({
                  fileUrl: r.url,
                  fileName: r.name,
                }))
              }
            />
            {signingBlock && (
              <SmartMessageSignModal
                documentId={signingBlock.documentId}
                documentTitle={signingBlock.label || message.text}
                onClose={() => setSigningBlock(null)}
                onSigned={async () => {
                  const block = signingBlock;
                  try {
                    await handleBlockRespond(block.id, { documentId: block.documentId });
                  } catch {
                    // Signature is on record; reopening the modal fires onSigned
                    // again (already signed) and retries the recording.
                  }
                  setSigningBlock(null);
                }}
              />
            )}
            {blockStatus === 'complete' && (
              <p className="text-sm mt-3 text-success font-medium">
                Your response has been recorded.
              </p>
            )}
            <div className="mt-3">
              <Link
                to={`/smart-message-form/${schoolId}/${message.smartMessageId}?student=${message.studentId}`}
                className="text-xs text-primary underline opacity-80 hover:opacity-100"
              >
                Open full view
              </Link>
            </div>
          </div>
        ) : (
          message.body && (
            <p className="text-sm mt-1 text-base-content/80">{message.body}</p>
          )
        )}

        {showMeta && (
          <div className="mt-3 pt-3 border-t border-base-200 space-y-1 text-sm">
            {message.senderName && (
              <div className="flex gap-2">
                <span className="w-16 text-[10px] uppercase tracking-wide font-bold text-base-content/60 pt-0.5">From</span>
                <span className="flex-1">{message.senderName}</span>
              </div>
            )}
            {studentLabel && (
              <div className="flex gap-2">
                <span className="w-16 text-[10px] uppercase tracking-wide font-bold text-base-content/60 pt-0.5">Re</span>
                <span className="flex-1">{studentLabel}</span>
              </div>
            )}
            {recipientLabel && (
              <div className="flex gap-2">
                <span className="w-16 text-[10px] uppercase tracking-wide font-bold text-base-content/60 pt-0.5">Sent to</span>
                <span className="flex-1">{recipientLabel}</span>
              </div>
            )}
          </div>
        )}

        {isBlockBased ? null : responded ? (
          <p className="text-sm mt-3 text-success font-medium">
            {confirmedResponse?.optionLabel
              ? `You responded: ${confirmedResponse.optionLabel}`
              : 'Your response has been recorded.'}
          </p>
        ) : message.responseFormat === 'attendance-reason' ? (
          <AttendanceReasonForm
            schoolId={schoolId}
            smartMessageId={message.smartMessageId}
            studentId={message.studentId}
            windowDates={message.metadata?.windowDates || []}
            onResponded={({ optionId }) => {
              const opt = message.responseOptions?.find((o) => o.id === optionId);
              setOptimisticResponse({
                optionId,
                optionLabel: opt?.label || optionId,
              });
            }}
          />
        ) : (
          message.responseOptions?.length > 0 && (
            <div className="flex gap-2 mt-3 flex-wrap">
              {message.responseOptions.map((option, index) => {
                const isPending = pendingOptId === option.id;
                return (
                  <button
                    key={option.id}
                    className={`btn btn-sm ${index === 0 ? 'btn-primary' : 'btn-outline'} ${responding && !isPending ? 'opacity-50' : ''}`}
                    onClick={() => handleRespond(option)}
                    disabled={responding}
                  >
                    {isPending && <span className="loading loading-spinner loading-xs" />}
                    {option.label}
                  </button>
                );
              })}
            </div>
          )
        )}

        {respondError && !responded && (
          <p className="text-xs mt-2 text-error">{respondError}</p>
        )}
      </div>
    </div>
  );
}
